import CloseIcon from "@mui/icons-material/Close";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { acceptDeclineMeetingApi, cancelMeetingApi, respondRescheduleApi } from "../../../API/schedule";
import { acceptDeniedMeeting, updateMeetingSuccess } from "../../../store/actions/schedule";
import theme from "../../../theme/theme";
import { formatDate } from "../../../utils/formateDate";
import { errorResponseHandler, successResponseHandler } from "../../../utils/reposeAlert";
import InviteesList from "../../Invitees/InviteesList";
import MeetingActions from "../../Meeting/MeetingActions";
import MeetingCompanyName from "../../Meeting/MeetingCompanyName";
import MeetingDate from "../../Meeting/MeetingDate";
import MeetingDescription from "../../Meeting/MeetingDescription";
import MeetingOrganizer from "../../Meeting/MeetingOrganizer";
import MeetingStatusBadge from "../../Meeting/MeetingStatusBadge";
import MeetingTitle from "../../Meeting/MeetingTitle";

const MeetingModal = ({
  openMeetingModal,
  closeMeetingModalHandler,
  meeting,
  toggleRescheduleModal,
  showShowAskForRescheduleModalHandler,
  onRefresh,
}) => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: {
      xs: "100%",
      md: "35rem",
    },
    // Cap the modal to the viewport and scroll inside it. Without this a long
    // description makes the modal taller than the screen, clipping the bottom
    // (the Accept/Cancel actions) with no way to reach them.
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 2,
    pt: 5,
  };

  const checkOwnMeeting = () => {
    if (meeting.ownerId === user.id) {
      return true;
    }
    return false;
  };

  const acceptDeclineMeetingHandler = async (answer) => {
    await acceptDeclineMeetingApi(answer, meeting.id)
      .then((response) => {
        if (response.status === 200) {
          successResponseHandler(`Updated`, "Invitation updated successfully");
          dispatch(acceptDeniedMeeting(meeting.id, user.id, response.data.status));
          // Refresh the meeting list + requests (an accepted request moves into
          // the list; a declined one leaves the requests).
          if (onRefresh) onRefresh();
          closeMeetingModalHandler();
        }
      })
      .catch((error) => {
        const msg = error.response.data;
        errorResponseHandler(`Failed`, msg);
      })
      .finally({});
  };

  const rescheduleMeetingHandler = () => {
    toggleRescheduleModal(true);
  };

  // Owner cancels the meeting (soft cancel). Invitees are notified by the
  // backend. On success the returned schedule (status: "cancelled") is pushed
  // into the store so the list/badge reflect it, then the modal closes.
  const cancelMeetingHandler = async () => {
    setCancelling(true);
    await cancelMeetingApi(meeting.id)
      .then((response) => {
        if (response.status === 200) {
          successResponseHandler("Cancelled", "Meeting cancelled successfully");
          if (response.data) {
            dispatch(updateMeetingSuccess(response.data));
          }
          if (onRefresh) onRefresh();
          setCancelDialogOpen(false);
          closeMeetingModalHandler();
        }
      })
      .catch((error) => {
        errorResponseHandler("Failed", error.response?.data || error.message);
      })
      .finally(() => {
        setCancelling(false);
      });
  };

  // Owner accepts/declines a pending reschedule request an invitee proposed.
  const respondRescheduleHandler = async (action) => {
    await respondRescheduleApi(meeting.id, action)
      .then((response) => {
        if (response.status === 200) {
          successResponseHandler("Done", response.data.message);
          if (onRefresh) onRefresh();
          closeMeetingModalHandler();
        }
      })
      .catch((error) => {
        errorResponseHandler("Failed", error.response?.data || error.message);
      });
  };

  // A pending reschedule request exists when the invitee proposed a new time.
  const hasRescheduleRequest =
    checkOwnMeeting() &&
    !!meeting.proposedStartDate &&
    !!meeting.rescheduleRequestedById;

  const askForRescheduleHandler = async () => {
    showShowAskForRescheduleModalHandler(true, meeting.id);
  };

  const checkDisableButton = () => {
    if (meeting.status === "pending") {
      return false;
    }
    return true;
  };

  // The current user's own invitation (if they were invited) and its status.
  const myInvitation = (meeting.invitations || []).find(
    (inv) => inv.registrationId === user?.id
  );
  const myStatus = myInvitation?.status; // pending | accepted | declined
  const iRequestedReschedule = meeting.rescheduleRequestedById === user?.id;

  return (
    <Modal
      open={openMeetingModal}
      onClose={closeMeetingModalHandler}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{
        zIndex: 10,
      }}
    >
      <Paper elevation={3} sx={modalStyle}>
        <Box style={{ position: "absolute", top: "0.5rem", left: "0.5rem" }}>
          <IconButton
            onClick={closeMeetingModalHandler}
            sx={{
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {/* <MeetingStatus status={meeting.meeting_status} /> */}
        <Box sx={{ position: "relative" }}>
          <MeetingTitle title={meeting.title} />
          <Box sx={{ mb: 1 }}>
            <MeetingStatusBadge meeting={meeting} userId={user?.id} />
          </Box>
          <MeetingDate date={`${formatDate(meeting.startDate, meeting.endDate)}`} />
          <MeetingCompanyName company={meeting.owner.company.name} />
          {user && (
            <MeetingOrganizer
              organizer={`${meeting.owner.firstName} ${meeting.owner.lastName} - (${meeting.owner.company.name}) - (${meeting.owner.position})`}
              user={user}
              meeting={meeting}
            />
            
          )}
          <MeetingDescription description={meeting.description ?? ""} />
          <InviteesList invitees={meeting.invitations} />
          {hasRescheduleRequest && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                p: 1.5,
                borderRadius: "8px",
                border: `1px solid ${theme.palette.warning.main}`,
                backgroundColor: "rgba(249, 168, 37, 0.08)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <EventRepeatIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
                <Typography sx={{ color: theme.palette.warning.main, fontWeight: 700, fontSize: 14 }}>
                  Reschedule Requested
                </Typography>
              </Box>
              <Typography sx={{ color: theme.palette.primary.main, fontSize: 14, mt: 0.5 }}>
                {`Proposed: ${formatDate(meeting.proposedStartDate, meeting.proposedEndDate)}`}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => respondRescheduleHandler("accept")}
                  sx={{
                    backgroundColor: theme.palette.success.main,
                    "&:hover": { backgroundColor: theme.palette.success.dark },
                    textTransform: "none",
                  }}
                >
                  Accept New Time
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => respondRescheduleHandler("decline")}
                  sx={{
                    color: theme.palette.error.main,
                    borderColor: theme.palette.error.main,
                    "&:hover": { borderColor: theme.palette.error.dark },
                    textTransform: "none",
                  }}
                >
                  Decline New Time
                </Button>
              </Box>
            </Box>
          )}
          <MeetingActions
            ownMeeting={checkOwnMeeting}
            myStatus={myStatus}
            iRequestedReschedule={iRequestedReschedule}
            acceptDeclineMeetingHandler={acceptDeclineMeetingHandler}
            askForRescheduleHandler={askForRescheduleHandler}
            rescheduleMeetingHandler={rescheduleMeetingHandler}
            cancelMeetingHandler={() => setCancelDialogOpen(true)}
            disabledButton={checkDisableButton}
            closeMeetingModalHandler={closeMeetingModalHandler}
          />
        </Box>

        {/* Owner cancel confirmation */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => (cancelling ? null : setCancelDialogOpen(false))}
          aria-labelledby="cancel-meeting-dialog-title"
          sx={{ zIndex: 20 }}
        >
          <DialogTitle id="cancel-meeting-dialog-title">Cancel Meeting</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to cancel this meeting? Invitees will be notified.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelling}
              color="primary"
              variant="outlined"
            >
              Keep Meeting
            </Button>
            <Button
              onClick={cancelMeetingHandler}
              disabled={cancelling}
              color="error"
              variant="contained"
            >
              Cancel Meeting
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Modal>
  );
};

export default MeetingModal;
